import { format, parseISO } from 'date-fns'

// ── Frontend types ──────────────────────────────────────────────────────

export interface WaveRow {
  id?: string | null
  waveNumber: number
  quantity: number | null
  percent: number | null
  shipDate: Date | null
  receiveDate: Date | null
}

export interface DistributionCenter {
  id: string
  name: string
  isAllDCs: boolean
  selectedDC: string
  rows: WaveRow[]
}

// ── API types (matching OpenAPI spec) ───────────────────────────────────

export type ApiDistributionType = 'PERCENT' | 'QUANTITY'
export type ApiDestinationType = 'STORE' | 'DC'

export interface WaveBreakRead {
  id?: string | null
  wave_number: number | null
  quantity: string
  percent: string
  ship_at: string | null
  receive_at: string | null
  notes?: string | null
  destination_type?: ApiDestinationType | null
  destination_id?: string | null
  distribution_type: ApiDistributionType
}

export interface WaveBreaksDestinationResponse {
  destination_type: ApiDestinationType | null
  destination_id: string | null
  rows: WaveBreakRead[]
}

export interface WaveBreaksFullResponse {
  distribution_type: ApiDistributionType
  items: WaveBreaksDestinationResponse[]
}

export interface WaveBreakCreate {
  wave_number?: number | null
  quantity: string
  percent: string
  ship_at: string | null
  receive_at: string | null
}

export interface CreateWaveBreaksDestination {
  destination_type: ApiDestinationType | null
  destination_id: string | null
  rows: WaveBreakCreate[]
}

export interface CreateWaveBreaksRequest {
  distribution_type?: ApiDistributionType | null
  items?: CreateWaveBreaksDestination[]
  source_version_id?: string | null
  source_line_item_id?: string | null
  apply_to_all_line_items?: boolean
}

export interface WaveBreakUpdate {
  id?: string | null
  wave_number?: number | null
  quantity: string
  percent: string
  ship_at: string | null
  receive_at: string | null
}

export interface UpdateWaveBreaksDestination {
  destination_type: ApiDestinationType | null
  destination_id: string | null
  rows: WaveBreakUpdate[]
}

export interface UpdateWaveBreaksRequest {
  distribution_type: ApiDistributionType
  items: UpdateWaveBreaksDestination[]
  apply_to_all_line_items?: boolean
}

// ── Mapping helpers ─────────────────────────────────────────────────────

const DIST_TYPE_TO_API: Record<'percentage' | 'absolute', ApiDistributionType> =
  {
    percentage: 'PERCENT',
    absolute: 'QUANTITY',
  }

const DIST_TYPE_FROM_API: Record<
  ApiDistributionType,
  'percentage' | 'absolute'
> = {
  PERCENT: 'percentage',
  QUANTITY: 'absolute',
}

function dateToString(date: Date | null): string | null {
  return date ? format(date, 'yyyy-MM-dd') : null
}

function stringToDate(str: string | null): Date | null {
  return str ? parseISO(str) : null
}

/** Convert API response → frontend local state */
export function mapApiToFrontend(response: WaveBreaksFullResponse): {
  distributionType: 'percentage' | 'absolute'
  distributionCenters: DistributionCenter[]
} {
  const distributionType = DIST_TYPE_FROM_API[response.distribution_type]

  const distributionCenters: DistributionCenter[] = response.items.map(
    (dest, index) => {
      const isAllDCs = !dest.destination_type && !dest.destination_id

      return {
        id: isAllDCs ? 'all-dcs' : `dc-api-${index}`,
        name: isAllDCs
          ? 'All Distribution Centers'
          : (dest.destination_id ?? ''),
        isAllDCs,
        selectedDC: dest.destination_id ?? '',
        rows: dest.rows.map(row => ({
          id: row.id,
          waveNumber: row.wave_number ?? 1,
          quantity: row.quantity ? Number(row.quantity) : null,
          percent: row.percent ? Number(row.percent) : null,
          shipDate: stringToDate(row.ship_at),
          receiveDate: stringToDate(row.receive_at),
        })),
      }
    }
  )

  distributionCenters.sort((a, b) =>
    a.isAllDCs === b.isAllDCs ? 0 : a.isAllDCs ? -1 : 1
  )

  return { distributionType, distributionCenters }
}

/** Convert frontend state → CreateWaveBreaksRequest */
export function mapFrontendToCreateRequest(
  distributionType: 'percentage' | 'absolute',
  dcs: DistributionCenter[],
  applyToAll: boolean
): CreateWaveBreaksRequest {
  const apiDistType = DIST_TYPE_TO_API[distributionType]

  return {
    distribution_type: apiDistType,
    items: dcs.map(dc => ({
      destination_type: dc.isAllDCs ? null : ('DC' as ApiDestinationType),
      destination_id: dc.isAllDCs ? null : dc.selectedDC,
      rows: dc.rows.map(row => ({
        wave_number: row.waveNumber,
        quantity: String(row.quantity ?? 0),
        percent: String(row.percent ?? 0),
        ship_at: dateToString(row.shipDate),
        receive_at: dateToString(row.receiveDate),
      })),
    })),
    apply_to_all_line_items: applyToAll,
  }
}

/** Convert frontend state → UpdateWaveBreaksRequest */
export function mapFrontendToUpdateRequest(
  distributionType: 'percentage' | 'absolute',
  dcs: DistributionCenter[],
  applyToAll: boolean
): UpdateWaveBreaksRequest {
  const apiDistType = DIST_TYPE_TO_API[distributionType]

  return {
    distribution_type: apiDistType,
    items: dcs.map(dc => ({
      destination_type: dc.isAllDCs ? null : ('DC' as ApiDestinationType),
      destination_id: dc.isAllDCs ? null : dc.selectedDC,
      rows: dc.rows.map(row => ({
        id: row.id ?? null,
        wave_number: row.waveNumber,
        quantity: String(row.quantity ?? 0),
        percent: String(row.percent ?? 0),
        ship_at: dateToString(row.shipDate),
        receive_at: dateToString(row.receiveDate),
      })),
    })),
    apply_to_all_line_items: applyToAll,
  }
}
